/**
 * search.engine.js - TMD Demand-Driven Search Logic (Flow 1)
 * * This engine prioritizes static JSON from the CDN, falling back to 
 * the regional mirror bridge for on-demand "baking" of lot data.
 */

const CDN_BASE_URL = "https://spec.trackmydown.com";

import { getApiUrl, IS_LOCALHOST, SUSTAINABILITY_DEV_MODE as DEV_MODE } from './url.utils';

// Check for inspect=1 in URL to enable verbose logging
const DEBUG_MODE = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('inspect') === '1';



/**
 * Normalizes lot data to the "Domain-Model" clean schema.
 */
// Helper to log only in DEV_MODE or DEBUG_MODE
const logDev = (msg, type = 'log', data = null) => {
  if (DEBUG_MODE) {
    switch (type) {
      case 'warn':
        console.warn(`[TMD_LOG]: ${msg}`, data || '');
        break;
      case 'error':
        console.error(`[TMD_LOG]: ${msg}`, data || '');
        break;
      default:
        console.log(`[TMD_LOG]: ${msg}`, data || '');
    }
  }
};

const logPhase = (msg, color = "#ccc") => {
  if (DEBUG_MODE) {
    console.log(`%c${msg}`, `color: ${color}; font-weight: bold`);
  }
};

const OPT_IN_PREFIXES = ['0502', '2101', '0204'];

/**
 * Normalizes lot data to the "Domain-Model" clean schema.
 */
export const normalizeData = (data, source = "Unknown") => {
  if (!data) return null;
  const fields = data.fields || data; 

  const get = (key, fallback = null) => {
    const val = fields[key];
    if (val === undefined || val === null) return fallback;

    // Handle Firestore REST API value wrappers
    if (typeof val === 'object' && !Array.isArray(val)) {
      if (val.stringValue !== undefined) return val.stringValue;
      if (val.integerValue !== undefined) return val.integerValue;
      if (val.doubleValue !== undefined) return val.doubleValue;
      if (val.booleanValue !== undefined) return val.booleanValue;
      
      if (val.arrayValue !== undefined) {
        const list = val.arrayValue.values || [];
        return list.map(item => {
          if (typeof item === 'object') {
            if (item.stringValue !== undefined) return item.stringValue;
            if (item.integerValue !== undefined) return item.integerValue;
            if (item.doubleValue !== undefined) return item.doubleValue;
            if (item.booleanValue !== undefined) return item.booleanValue;
          }
          return item;
        });
      }

      if (val.mapValue !== undefined) {
        const map = val.mapValue.fields || {};
        const result = {};
        Object.keys(map).forEach(mapKey => {
          const mapField = map[mapKey];
          if (typeof mapField === 'object') {
            if (mapField.stringValue !== undefined) result[mapKey] = mapField.stringValue;
            else if (mapField.integerValue !== undefined) result[mapKey] = mapField.integerValue;
            else if (mapField.doubleValue !== undefined) result[mapKey] = mapField.doubleValue;
            else if (mapField.booleanValue !== undefined) result[mapKey] = mapField.booleanValue;
            else result[mapKey] = mapField;
          } else {
            result[mapKey] = mapField;
          }
        });
        return result;
      }
    }

    return val;
  };

  const prefix = get('prefix') || get('Prefix') || '';
  const lotNum = get('lot_number') || get('Display_Lot');
  const dispLot = get('display_lot') || get('Display_Lot') || get('lot_number');

  return {
    source: source, 
    lot_number: lotNum || dispLot,
    display_lot: dispLot || lotNum,
    production_lot: get('production_lot') || get('Production_Lot'),
    prefix: prefix,
    stated_fp: get('stated_fp') || get('statedFP'),
    verified_fp: get('verified_fp') || get('verfiedFP'),
    stated_fp_gb: get('stated_fp_gb'),
    verified_fp_gb: get('verified_fp_gb'),
    turbidity: (() => {
      const val = (get('turbidity') || get('turbulity') || '').toString().trim();
      if (!val) return '';
      return val.toUpperCase().includes('M') ? val : `${val}MM`;
    })(),
    oxygen: get('oxygen') || get('oxgen') || '',
    down_cluster: (get('down_cluster') || get('cluster') || '').toString().replace('%', '').trim(),
    down_fiber: (get('down_fiber') || get('down') || '').toString().replace('%', '').trim(),
    feather_fiber: (get('feather_fiber') || get('feather') || '').toString().replace('%', '').trim(),
    waterfowl_feather: (get('waterfowl_feather') || get('waterfowl') || '').toString().replace('%', '').trim(),
    broken_feather: (get('broken_feather') || get('brokenfeather') || '').toString().replace('%', '').trim(),
    quill: (get('quill') || '0.0').toString().replace('%', '').trim(),
    land_fowl: (get('land_fowl') || get('landfowl') || '').toString().replace('%', '').trim(),
    residue: (get('residue') || '0.0').toString().replace('%', '').trim(),
    rptLink: get('rptLink') || get('RPTLink'),
    treatment: (() => {
      const t = get('treatment') || get('Treatment');
      if (t === false || t === null || t === undefined || t === "none" || t === "false") return [];
      return Array.isArray(t) ? t.map(String) : [String(t)];
    })(),
    recycled: String(get('recycled', false)).toLowerCase() === 'yes' || get('recycled') === true || get('Recycled') === 'Yes',
    rds: String(get('rds', false)).toLowerCase() === 'yes' || get('rds') === true || get('RDS') === 'Yes',
    rsd: OPT_IN_PREFIXES.includes(prefix),
    down_type: get('down_type') || get('DownType'),
    origin: (() => {
      const rawOrigin = get('origin') || get('Origin');
      if (Array.isArray(rawOrigin)) return rawOrigin;
      if (typeof rawOrigin === 'string' && rawOrigin.trim() !== '') {
        return rawOrigin.split(',').map(s => s.trim());
      }
      return ['China']; 
    })(),
    origin_percent: (get('origin_percent') || '').toString().replace('%', '').trim(),
    impact: get('impact') || null,
    showImpact: get('showImpact', false),
    hideCalculator: get('hideCalculator', true),
    chinaDomestic: get('chinaDomestic', false),
    p_code: get('p_code', ''),
    fat_oil: get('fat_oil', ''),
    facility: get('facility') || get('Facility', ''), 
    verified_at: get('verified_at') || new Date().toISOString()
  };
};

/**
 * Helper to determine facility from lot number format.
 */
const getFacilityCode = (lot) => {
  const singleLot = lot.length > 5 ? lot.slice(4, 5) : lot.slice(4);
  if (!isNaN(+singleLot.charAt(0))) return "la";
  if (singleLot.charAt(0).toUpperCase() === "M") return "mt";
  return "cn";
};

export const fetchLotData = async (lotNumber) => {
  const lot = String(lotNumber).toUpperCase();
  logDev(`Initiating strict fetch for ${lot}`);

  // 1. Try CDN Check (Phase A)
  try {
    const cdnUrl = `${CDN_BASE_URL}/${lot}.json`;
    logDev(`[PHASE A] Checking CDN (Full): ${cdnUrl}`);
    let cdnResponse = await fetch(cdnUrl);
    
    if (!cdnResponse.ok && lot.length > 4) {
      const shortLot = lot.slice(4);
      const shortUrl = `${CDN_BASE_URL}/${shortLot}.json`;
      logDev(`[PHASE A.2] Checking CDN (Short): ${shortUrl}`);
      cdnResponse = await fetch(shortUrl);
    }

    if (cdnResponse.ok) {
      const data = await cdnResponse.json();
      logDev("RAW CDN Data:", 'log', data);
      logPhase(`[PHASE A SUCCESS] Resolved via CDN`, "#4CAF50");
      const normalized = normalizeData(data, "CDN");
      logDev("NORMALIZED CDN Data:", 'log', normalized);
      return normalized; // HALT: Data found in CDN
    }
  } catch (e) {
    logDev(`[PHASE A ERROR] CDN unavailable: ${e.message}`, "error");
  }
  
  // 2. Try Firestore Proxy (Phase B.5)
  try {
    logDev(`[PHASE B.5] Checking Firestore Proxy: core/fire.php?lot=${lot}`);
    const proxyResponse = await fetch(getApiUrl(`core/fire.php?lot=${lot}`));
    
    if (proxyResponse.ok) {
      const data = await proxyResponse.json();
      if (data && !data.message && data.status !== "error") {
        logDev("RAW Proxy Data:", 'log', data);
        logPhase(`[PHASE B.5 HIT] Resolved via Proxy`, "#FF9800");
        const normalized = normalizeData(data, "Firestore Proxy");
        logDev("NORMALIZED Proxy Data:", 'log', normalized);
        return normalized; // HALT: Data found in Proxy
      }
    }
  } catch (e) {
    logDev(`[PHASE B.5 ERROR] Proxy failure: ${e.message}`, "error");
  }
  
  // 3. Firestore Direct (Phase C)
  try {
    const { getFirestore, doc, getDoc } = await import("firebase/firestore");
    const db = getFirestore();
    const prodFac = getFacilityCode(lot);
    const docRef = doc(db, `afd/${prodFac}/${lot}/verified`);
    const snapshot = await getDoc(docRef);
    
    if (snapshot.exists()) {
      const rawData = snapshot.data();
      logDev("RAW Firestore Data:", 'log', rawData);
      logPhase(`[PHASE C SUCCESS] Resolved via SDK`, "#9C27B0");
      const normalized = normalizeData(rawData, "Firestore");
      logDev("NORMALIZED Firestore Data:", 'log', normalized);
      return normalized; // HALT: Data found in Firestore Direct
    }
  } catch (e) {
    logDev(`Phase C failed: ${e.message}`, "error");
  }
    
  // --- OFFLINE FALLBACK (Phase 4) ---
  // If we reach this point, check for local mock data if in dev mode
  if (process.env.REACT_APP_DISABLE_GA || IS_LOCALHOST) {
    console.log('localModern');
    try {
      const debugUrl = `/core/config/1535L25F0243.json`;
      const response = await fetch(debugUrl);
      if (response.ok) {
        const data = await response.json();
        return normalizeData(data, "Local Fallback");
      }
    } catch (e) {      
    }
  }

  // 4. Final Fail Halt
  // If we reach this point, no modern data was found. 
  // The App will proceed to the Legacy fallback.
  return null; // Explicitly return null if no data found after all attempts
};

export async function ddException(lotData) {
  let response = { vfp: false, land_fowl: false, quill: false, origin_exception: false };
  if (!lotData) return response;

  if (lotData.verified_fp == "/" || lotData.verified_fp == "0") response.vfp = true;
  if (lotData.land_fowl == "0.0") response.land_fowl = true;
  if (lotData.quill == "0.0") response.quill = true;

  const downType = lotData.down_type;
  const origins = lotData.origin;

  if (downType && origins && Array.isArray(origins)) {
    if (downType.toLowerCase().includes("white duck down")) {
      if (!origins.some(origin => origin.toLowerCase().includes("europe"))) response.origin_exception = true;
    } else if (downType.toLowerCase().includes("white goose down")) {
      if (!origins.some(origin => origin.toLowerCase().includes("hungary"))) response.origin_exception = true;
    }
  }
  return response;
}